import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { ampedService, type AmpedPlanRecord } from '@/services/ampedService';
import { planService } from '@/services/planService';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import { redirectToShopify } from '@/lib/shopifyRedirect';

type TierName = 'STARTER' | 'PRO' | 'ELITE';

interface TierCopy {
  displayName: string;
  tagline: string;
  isPopular: boolean;
  sections: Array<{
    label: string;
    features: string[];
  }>;
}

// Marketing copy stays client-side; pricing + tier metadata comes from the API.
const TIER_COPY: Record<TierName, TierCopy> = {
  STARTER: {
    displayName: 'AMPED STARTER',
    tagline: 'Your brand, everywhere they scroll.',
    isPopular: false,
    sections: [
      {
        label: 'CORE',
        features: [
          'Story viewing automation',
          'Welcome DM to every new follower',
        ],
      },
      {
        label: 'REPORTING',
        features: [
          'Daily Telegram analytics report',
          'Reactions, story likes & DM stats',
        ],
      },
      {
        label: 'ONBOARDING',
        features: ['White-glove account setup', 'Onboarding call if needed'],
      },
    ],
  },
  PRO: {
    displayName: 'AMPED PRO',
    tagline: 'Stop waiting for leads. Go get them.',
    isPopular: true,
    sections: [
      {
        label: 'EVERYTHING IN STARTER, PLUS',
        features: [
          'Lead scraping — 8,000 emails & phones from competitor accounts or keywords',
          'Reply to pending DMs (reactivates warm leads)',
          'Close Friends list — 7,500 accounts, auto-rotates every 45 days',
          'Windfall Likes — attracts inbound from potential customers & affiliates',
        ],
      },
      {
        label: 'REPORTING',
        features: ['Expanded daily Telegram metrics'],
      },
    ],
  },
  ELITE: {
    displayName: 'AMPED ELITE',
    tagline: "Your brand runs Instagram. Instagram doesn't run you.",
    isPopular: false,
    sections: [
      {
        label: 'EVERYTHING IN PRO, PLUS',
        features: [
          'AI DM — 24/7 intelligent auto-responses that qualify leads',
          'Ambassador or second account included free',
          '15 keyword auto-DM triggers — auto-responds to comments & DMs to push specific offers',
        ],
      },
      {
        label: 'SUPPORT',
        features: ['Priority support', 'Dedicated Amped account manager'],
      },
    ],
  },
};

export default function ReveshareAmpedPricing() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [subscribingPlan, setSubscribingPlan] = useState<TierName | null>(null);
  const [isConfirmingCancel, setIsConfirmingCancel] = useState(false);

  const { data: currentAmped } = useQuery({
    queryKey: ['amped-current-profile'],
    queryFn: () => ampedService.getCurrentProfile(),
  });

  const { data: plans, isLoading: isLoadingPlans } = useQuery({
    queryKey: ['amped-plans'],
    queryFn: () => ampedService.listPlans(),
  });

  const { data: baseSubscription, isLoading: isLoadingBaseSubscription } = useQuery({
    queryKey: ['subscription'],
    queryFn: () => planService.getCurrentSubscription(),
  });

  const hasActiveBaseSubscription = Boolean(baseSubscription?.data?.active);

  const cancelMutation = useMutation({
    mutationFn: () => ampedService.cancel(),
    onSuccess: (response) => {
      if (response.confirmationUrl) {
        toast.success('Redirecting to Shopify to confirm cancellation…');
        redirectToShopify(response.confirmationUrl);
        return;
      }
      toast.success('Reveshare Amped cancelled.');
      queryClient.invalidateQueries({ queryKey: ['amped-current-profile'] });
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
    },
    onError: (error: unknown) => {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? 'Failed to cancel Amped. Please try again.';
      toast.error(message);
    },
  });

  async function handleGetStarted(plan: AmpedPlanRecord) {
    setSubscribingPlan(plan.name);
    try {
      const response = await ampedService.subscribe(plan.name);
      if (response.confirmationUrl) {
        redirectToShopify(response.confirmationUrl);
      } else {
        toast.error('Unable to start checkout. Please try again.');
      }
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? 'Something went wrong. Please try again.';
      toast.error(message);
    } finally {
      setSubscribingPlan(null);
    }
  }

  const hasActiveAmped = Boolean(currentAmped?.currentAmpedPlan);

  return (
    <div className="min-h-screen bg-card py-16 px-4">
      {/* Header */}
      <div className="text-center max-w-2xl mx-auto mb-12">
        <p className="text-xs font-semibold text-brand-600 uppercase tracking-widest mb-3">
          REVESHARE AMPED
        </p>
        <h1 className="text-5xl font-bold text-foreground mb-3">
          Instagram growth,
        </h1>
        <h1 className="text-5xl font-bold text-brand-600 mb-6">
          built into your store.
        </h1>
        <p className="text-muted-foreground text-base">
          Automate your brand's Instagram presence — from story views to DMs to
          lead capture — all white-glove set up by our team.
        </p>
      </div>

      {!isLoadingBaseSubscription && !hasActiveBaseSubscription && (
        <div className="max-w-3xl mx-auto mb-8 rounded-xl border border-amber-200 bg-amber-50 p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-amber-900 mb-1">
                An active Reveshare plan is required
              </p>
              <p className="text-sm text-amber-800">
                Reveshare Amped is an add-on to your base Reveshare subscription. Start a Reveshare plan first, then come back to add Amped.
              </p>
            </div>
            <button
              onClick={() => navigate('/brand/pricing')}
              className="shrink-0 rounded-md border border-amber-300 bg-card px-3 py-1.5 text-sm font-medium text-amber-900 hover:bg-amber-100"
            >
              View Reveshare plans
            </button>
          </div>
        </div>
      )}

      {currentAmped?.profile && (
        <div className="max-w-3xl mx-auto mb-8 rounded-xl border border-brand-100 bg-brand-50/60 p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold text-brand-700 uppercase tracking-wide mb-1">
                Current Reveshare Amped Profile
              </p>
              <p className="text-sm text-muted-foreground">
                {currentAmped.profile.firstName} {currentAmped.profile.lastName} ({currentAmped.profile.email})
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Plan: {currentAmped.currentAmpedPlan ?? 'Not subscribed yet'}
              </p>
            </div>
            {hasActiveAmped && (
              <button
                onClick={() => setIsConfirmingCancel(true)}
                disabled={cancelMutation.isPending}
                className="shrink-0 text-sm font-medium text-red-600 hover:text-red-700 underline-offset-2 hover:underline disabled:opacity-50"
              >
                Cancel subscription
              </button>
            )}
          </div>
        </div>
      )}

      {/* Plan cards */}
      <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
        {isLoadingPlans || !plans
          ? Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="h-[500px] rounded-lg border-2 border-border bg-card p-6 animate-pulse"
              />
            ))
          : plans.map((plan) => {
              const copy = TIER_COPY[plan.name];
              if (!copy) return null;
              const isCurrent = currentAmped?.currentAmpedPlan === plan.name;
              return (
                <div
                  key={plan.id}
                  className={`relative flex flex-col rounded-lg border-2 p-6 ${
                    copy.isPopular
                      ? 'border-brand-600 bg-card shadow-lg'
                      : 'border-border bg-card'
                  }`}
                >
                  {copy.isPopular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="bg-brand-600 text-white text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wide">
                        MOST POPULAR
                      </span>
                    </div>
                  )}

                  <div className="mb-4">
                    <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-2">
                      {copy.displayName}
                    </p>
                    <div className="flex items-baseline gap-1">
                      <span className="text-5xl font-bold text-foreground">
                        ${plan.price}
                      </span>
                      <span className="text-muted-foreground text-sm">/mo</span>
                    </div>
                    <p className="text-sm italic text-muted-foreground mt-2">{copy.tagline}</p>
                  </div>

                  <hr className="border-border mb-4" />

                  <div className="flex-1 space-y-4 mb-6">
                    {copy.sections.map((section) => (
                      <div key={section.label}>
                        <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-2">
                          {section.label}
                        </p>
                        <ul className="space-y-1.5">
                          {section.features.map((feature) => (
                            <li key={feature} className="flex items-start gap-2 text-sm text-muted-foreground">
                              <span className="text-brand-600 font-bold mt-0.5 shrink-0">✓</span>
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={() => handleGetStarted(plan)}
                    disabled={
                      subscribingPlan === plan.name ||
                      isCurrent ||
                      !hasActiveBaseSubscription
                    }
                    className={`w-full py-2.5 px-4 rounded-md border-2 text-sm font-semibold transition-colors ${
                      copy.isPopular
                        ? 'bg-brand-600 border-brand-600 text-white hover:bg-brand-700'
                        : 'bg-card border-brand-600 text-brand-600 hover:bg-brand-50'
                    } disabled:opacity-60 disabled:cursor-not-allowed`}
                  >
                    {isCurrent
                      ? 'Current plan'
                      : subscribingPlan === plan.name
                        ? 'Processing...'
                        : !hasActiveBaseSubscription
                          ? 'Base plan required'
                          : hasActiveAmped
                            ? 'Switch to this tier'
                            : 'Get started'}
                  </button>
                </div>
              );
            })}
      </div>

      {/* Telegram section */}
      <div className="max-w-3xl mx-auto bg-muted rounded-xl p-6 mb-8">
        <p className="font-bold text-foreground mb-2">
          Daily growth reports, straight to Telegram.
        </p>
        <p className="text-sm text-muted-foreground">
          Every morning you'll get a full breakdown — stories watched, reactions
          fired, DMs sent, leads analyzed — so you always know exactly what's
          working.
        </p>
      </div>

      {/* Footer note */}
      <p className="text-center text-sm text-muted-foreground max-w-2xl mx-auto">
        All plans include white-glove setup. No technical knowledge needed.
        Plans are add-ons to your existing RevShare subscription.
      </p>

      <ConfirmationModal
        isOpen={isConfirmingCancel}
        onClose={() => setIsConfirmingCancel(false)}
        onConfirm={() => {
          setIsConfirmingCancel(false);
          cancelMutation.mutate();
        }}
        title="Cancel Reveshare Amped?"
        message={`You'll be redirected to Shopify to confirm. Your ${currentAmped?.currentAmpedPlan ?? ''} add-on will be removed while your base Reveshare plan stays active.`}
        confirmText="Cancel Amped"
        cancelText="Keep my plan"
        variant="danger"
        isLoading={cancelMutation.isPending}
      />
    </div>
  );
}
